import React, { useMemo } from 'react';
import { buildImportIntelligenceSummary } from '../../utils/importIntelligence';
import type { ParsedModification } from './importReviewTypes';

interface ImportIntelligenceSummaryProps {
  modifications: ParsedModification[];
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

export const ImportIntelligenceSummary: React.FC<ImportIntelligenceSummaryProps> = ({
  modifications,
  collapsed = false,
  onToggleCollapsed,
}) => {
  const summary = useMemo(
    () => buildImportIntelligenceSummary(modifications),
    [modifications]
  );

  if (modifications.length === 0) return null;

  const detectedItems = [
    { label: 'parts', count: summary.modificationCount, show: summary.modificationCount > 0 },
    { label: 'categories', count: summary.categoryCount, show: summary.categoryCount > 0 },
    { label: 'sections', count: summary.sectionCount, show: summary.sectionCount > 0 },
    { label: 'brands', count: summary.brandCount, show: summary.brandCount > 0 },
    {
      label: 'with specifications',
      count: summary.specsCount,
      show: summary.specsCount > 0,
    },
  ].filter((item) => item.show);

  return (
    <section className="rounded-xl border border-orange-500/25 bg-gradient-to-br from-[#141414]/90 to-[#0a0a0a]/80 overflow-hidden">
      <div className="px-4 sm:px-5 py-4 border-b border-white/[0.08]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-orange-400/90 font-medium mb-1">
              Organization summary
            </p>
            <h3 className="text-lg sm:text-xl font-semibold text-white leading-snug">
              How Bilt grouped your parts
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Review the structure below. Nothing is saved until you add them to your build.
            </p>
          </div>
          {onToggleCollapsed && (
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="shrink-0 text-xs text-gray-400 hover:text-white px-2 py-1 rounded border border-white/[0.10]"
            >
              {collapsed ? 'Show summary' : 'Hide summary'}
            </button>
          )}
        </div>
      </div>

      {!collapsed && (
        <div className="px-4 sm:px-5 py-4 space-y-5">
          {detectedItems.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Detected
              </p>
              <ul className="space-y-1.5">
                {detectedItems.map(({ label, count }) => (
                  <li key={label} className="flex items-center gap-2 text-sm text-gray-200">
                    <span className="text-emerald-400" aria-hidden>
                      ✓
                    </span>
                    <span>
                      {count} {label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {summary.organized.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Organized
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {summary.organized.map(({ category, sections, moreSectionCount }) => (
                  <div
                    key={category}
                    className="rounded-lg border border-white/[0.10] bg-[#141414]/50 px-3 py-2.5"
                  >
                    <p className="text-sm font-semibold text-white mb-1.5">{category}</p>
                    {sections.length > 0 ? (
                      <ul className="space-y-0.5">
                        {sections.map((section) => (
                          <li key={section} className="text-xs text-gray-400 pl-2 border-l border-white/[0.10]">
                            {section}
                          </li>
                        ))}
                        {moreSectionCount > 0 && (
                          <li className="text-xs text-gray-500 pl-2">
                            + {moreSectionCount} more section{moreSectionCount === 1 ? '' : 's'}
                          </li>
                        )}
                      </ul>
                    ) : (
                      <p className="text-xs text-gray-500">General parts</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
